import * as Sentry from '@sentry/vue'
import { HttpError } from './errors'

/**
 * HTTP statuses that are expected business/user-side responses, not bugs.
 * Skipping them keeps Sentry focused on real defects.
 *
 *   401 — access token expired. http.ts retries via refresh; a definitive
 *         logout is captured separately by `captureForcedLogout`.
 *   403 — user lacks permission for the resource. Not actionable from FE.
 *   404 — resource missing (deleted item, stale id in URL). User-side.
 *   417 — Django REST custom "expectation failed" — backend signals a
 *         business-rule rejection (e.g. dashboard-not-ready). Expected flow.
 *   419 — auth/CSRF expired. Handled by token refresh path.
 *   429 — rate limit. Already surfaced via the `api-rate-limit` CustomEvent
 *         dispatched in http.ts; the cooldown UX is the actionable signal.
 *
 * 5xx is NOT in this list — those are captured at the network layer
 * (`http.ts`) with `_sentryReported = true`, so this function naturally
 * skips them via the early-return below.
 */
const SKIP_STATUSES = new Set<number>([401, 403, 404, 417, 419, 429])

export interface ReportContext {
  source: 'query' | 'mutation'
  key?: readonly unknown[]
}

/**
 * Forward an API error to Sentry with structured context, unless the status
 * code is a well-known "expected" one. Non-HttpError errors (network failures,
 * TypeError from fetch, aborts, or anything unexpected) are always captured.
 *
 * Safe to call in dev mode — `Sentry.captureException` is a silent no-op when
 * the SDK has not been initialized.
 */
export function reportApiError(error: unknown, context: ReportContext): void {
  // Already captured at the network layer (http.ts) — skip to avoid duplicate
  // events. Applies to both HttpError (5xx response) and raw fetch failures
  // (TypeError, offline, nginx 502 before response).
  if (
    error
    && typeof error === 'object'
    && (error as { _sentryReported?: boolean })._sentryReported
  ) {
    return
  }

  if (error instanceof HttpError) {
    if (SKIP_STATUSES.has(error.status)) {
      return
    }
    captureHttpError(error, context)
    return
  }

  captureUnknownError(error, context)
}

function captureHttpError(error: HttpError, context: ReportContext): void {
  const pathname = extractPathname(error.url)
  const level: Sentry.SeverityLevel = error.status >= 500 ? 'error' : 'warning'

  Sentry.captureException(error, {
    level,
    tags: {
      error_type: 'http_error',
      source: context.source,
      http_status: String(error.status),
      http_method: error.method,
    },
    contexts: {
      request: {
        url: error.url,
        method: error.method,
        status_code: error.status,
      },
    },
    extra: {
      query_key: context.key,
      response_status_text: error.response.statusText,
    },
    // Group by method + path + status so every endpoint/status combo lives
    // in its own Sentry issue instead of being lumped under a single
    // "HTTP Error: 500" bucket.
    fingerprint: ['http-error', error.method, pathname, String(error.status)],
  })
}

function captureUnknownError(error: unknown, context: ReportContext): void {
  Sentry.captureException(error, {
    level: 'error',
    tags: {
      error_type: 'network_or_unknown',
      source: context.source,
    },
    extra: {
      query_key: context.key,
    },
  })
}

function extractPathname(url: string): string {
  try {
    return new URL(url).pathname
  }
  catch {
    return url
  }
}

/**
 * Record a low-noise breadcrumb for auth-flow events. Invisible in Sentry on
 * its own — only attached to events that get captured later (e.g. a forced
 * logout message).
 */
export function addAuthBreadcrumb(
  event: string,
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({
    category: 'auth',
    message: event,
    level: 'info',
    data,
  })
}

export type AuthTelemetryReason
  = | 'no_refresh_token'
    | 'refresh_invalid'
    | 'refresh_transient_failure'
    | 'retry_still_unauthorized'

/**
 * Capture a forced logout into Sentry as a `warning` message so we can see
 * how often mobile users are being kicked out. Status codes 401/403 are
 * skipped by `reportApiError`, so this is the ONLY signal we get.
 *
 * Sampled at 100% — forced logouts are rare enough that full volume is fine
 * and gives us complete replay coverage (replaysOnErrorSampleRate = 1.0).
 */
export function captureForcedLogout(context: {
  reason: AuthTelemetryReason
  originalStatus: number
  originalUrl: string
  refreshStatus?: number
  hadRefreshToken: boolean
}): void {
  const tags: Record<string, string> = {
    event_type: 'forced_logout',
    reason: context.reason,
    original_status: String(context.originalStatus),
    had_refresh_token: String(context.hadRefreshToken),
  }

  if (context.refreshStatus !== undefined) {
    tags.refresh_status = String(context.refreshStatus)
  }

  Sentry.captureMessage('forced_logout', {
    level: 'warning',
    tags,
    contexts: {
      auth: {
        original_url: context.originalUrl,
        original_status: context.originalStatus,
        refresh_status: context.refreshStatus,
        had_refresh_token: context.hadRefreshToken,
        viewport_width: typeof window !== 'undefined' ? window.innerWidth : undefined,
        viewport_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      },
    },
    fingerprint: ['forced-logout', context.reason],
  })
}
